import { scaleLinear, scaleTime } from '@visx/scale';
import { extent } from 'd3-array';
import type { ChartDimensions } from '../types';
import type {
  NormalisedDataPoint,
  NormalisedValuation,
} from '../utils/normaliseChartData';

interface UseChartScalesProps {
  dimensions: ChartDimensions;
  keys: number[];
  normalisedEntities: Record<number, NormalisedDataPoint>;
  normalisedValuations?: NormalisedValuation[];
  periodStart: number;
  periodEnd: number;
}

export const useChartScales = ({
  dimensions,
  keys,
  normalisedEntities,
  normalisedValuations,
  periodStart,
  periodEnd,
}: UseChartScalesProps) => {
  // Set up the time scale for X axis (horizontal)
  const xScale = scaleTime<number>({
    range: [dimensions.paddingX, dimensions.width - dimensions.paddingX],
    domain: [periodStart, keys.length > 0 ? keys[keys.length - 1] : periodEnd],
  });

  // Find the minimum and maximum percentages to set chart boundaries
  const values = [
    ...Object.values(normalisedEntities).map((e) => e.value),
    ...(normalisedValuations
      ?.map((v) => v.fairValue)
      .filter((v): v is number => v !== null && !isNaN(v)) || []),
  ];

  const [minY, maxY] =
    values.length > 0
      ? extent(values.filter((v): v is number => typeof v === 'number')) ?? [
          0, 0,
        ]
      : [0, 0];

  // Set up the percentage scale for Y axis (vertical)
  const yScale = scaleLinear<number>({
    range: [dimensions.paddingY, dimensions.height - dimensions.paddingBottom],
    domain: [
      Math.max(maxY || 0, 0), // Highest percentage
      Math.min(minY || 0, 0), // Lowest percentage
    ],
    clamp: true,
  });

  return { xScale, yScale };
};
